import { React, useEffect, useState } from "react";
import { Card, CardContent, styled, Typography } from "@mui/material";
import { S3DataMain } from "../../S3Data";
import { hasPhotoForDay, hasTextForDay } from "../../photoDays";

const StyledMainContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "min(1120px, 100%)",
  alignItems: "center",
  gap: "1.5rem",
}));

const IntroCard = styled("section")(({ theme }) => ({
  width: "100%",
  padding: "1.8rem 2rem",
  borderRadius: "2rem",
  background: "rgba(255, 248, 239, 0.82)",
  border: "1px solid rgba(159, 104, 72, 0.14)",
  boxShadow: "0 20px 48px rgba(90, 60, 41, 0.1)",
  [theme.breakpoints.down("sm")]: {
    padding: "1.2rem 1rem",
    borderRadius: "1.5rem",
  },
}));

const Eyebrow = styled(Typography)(({ theme }) => ({
  fontSize: "0.82rem",
  textTransform: "uppercase",
  letterSpacing: "0.22em",
  color: theme.palette.primary.main,
}));

const Heading = styled(Typography)(({ theme }) => ({
  fontFamily: "'Fira Sans Extra Condensed', sans-serif",
  fontSize: "clamp(2.3rem, 5vw, 3.8rem)",
  lineHeight: 0.95,
  marginTop: "0.35rem",
  color: theme.palette.text.primary,
}));

const IntroText = styled(Typography)(({ theme }) => ({
  marginTop: "0.85rem",
  maxWidth: "40rem",
  lineHeight: 1.8,
  color: theme.palette.text.secondary,
}));

const StyledArchivesCard = styled(Card)(({ theme }) => ({
  width: "100%",
  borderRadius: "2rem",
  background: "rgba(255, 251, 246, 0.88)",
  boxShadow: "0 16px 36px rgba(95, 58, 38, 0.08)",
  border: "1px solid rgba(159, 104, 72, 0.12)",
}));

const StyledArchiveContent = styled(CardContent)(({ theme }) => ({
  padding: "2rem",
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    padding: "1.2rem 1rem",
  },
}));

const DayTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Fira Sans Extra Condensed', sans-serif",
  fontSize: "2.5rem",
  color: theme.palette.text.primary,
}));

const DayText = styled(Typography)(({ theme }) => ({
  fontSize: "1.02rem",
  lineHeight: 1.8,
  color: theme.palette.text.secondary,
  maxWidth: "36rem",
  margin: "0.8rem auto 0",
}));

const StyledArchiveImage = styled("img")({
  borderRadius: "1.5rem",
  margin: "1.5rem auto 0",
  objectFit: "cover",
  boxShadow: "0 18px 36px rgba(93, 62, 44, 0.14)",
});

function Archives() {
  let day = 121;
  const baseUrl = "https://daily-sarah.s3.amazonaws.com/";
  const imgExt = "photos/";
  const [text, setText] = useState([]);
  const [img, setImg] = useState([]);

  useEffect(() => {
    async function fetchData() {
      const imageData = [];
      const textData = [];
      for (let i = 1; i <= day; i++) {
        const txtData = hasTextForDay(i)
          ? await S3DataMain(`text/${i}.txt`)
          : null;

        imageData.push(hasPhotoForDay(i) ? `${baseUrl}${imgExt}${i}` : "");
        textData.push(txtData ? txtData.Body.toString() : "");
      }
      setText(textData);
      setImg(imageData);
    }
    fetchData();
  }, [day]);

  const DynamicCardContent = () => {
    const cardContent = [];

    for (let i = 0; i < 121; i++) {
      cardContent.push(
        <StyledArchivesCard key={i}>
          <StyledArchiveContent>
            <DayTitle>Day {i + 1}</DayTitle>
            <DayText>{text[i]}</DayText>
            {img[i] && (
              <StyledArchiveImage
                alt=""
                src={img[i]}
                style={{
                  width: "min(100%, 360px)",
                  maxHeight: "320px",
                }}
              />
            )}
          </StyledArchiveContent>
        </StyledArchivesCard>
      );
    }

    return cardContent;
  };

  return (
    <StyledMainContainer>
      <IntroCard>
        <Eyebrow>Archives</Eyebrow>
        <Heading>Every saved day</Heading>
        <IntroText>
          An archive layout with the
          same content preserved in its original order.
        </IntroText>
      </IntroCard>
      <DynamicCardContent numCards={day} />
    </StyledMainContainer>
  );
}

export default Archives;
